import BybVrCard from "@Components/common/byb-ds/cards/byb-vr-card";
import MonthlyTraining from "@Components/game/progress/stats/monthly-training";
import TimeDisplay from "@Components/game/progress/stats/time-display";
import WeeklyTraining from "@Components/game/progress/stats/weekly-training";
import { useProgress } from "@Context/game/progress/progress-context";
import useI18n from "@i18n";
import { Grid, Skeleton, Stack, Typography, useTheme } from "@mui/material";

interface ITrainingStatsSection {}

const TrainingStatsSection = ({}: ITrainingStatsSection) => {
  const theme = useTheme();
  const { i18n } = useI18n();
  const { totalSessions, totalTrainingTime, isTotalSessionsLoading, isTotalTimeLoading, trainingStreakWeeks, isStreakLoading } = useProgress();

  const avgSessionDuration = totalSessions > 0 ? Math.floor(totalTrainingTime / totalSessions) : 0;
  const avgSessionMinutes = Math.round(avgSessionDuration / 60);
  const avgSessionSeconds = avgSessionDuration % 60;

  const totalHours = Math.floor(totalTrainingTime / 3600);
  const totalMinutes = Math.round((totalTrainingTime % 3600) / 60);

  return (
    <Stack gap={2}>
      <Typography variant="textLg700" color="text.primary">
        {i18n("progress.section.training-stats")}
      </Typography>

      <Grid container alignItems="stretch" spacing={2}>
        <Grid item xs={3} display="flex">
          <WeeklyTraining />
        </Grid>

        <Grid item xs={3} display="flex">
          <MonthlyTraining />
        </Grid>

        <Grid item xs={6} display="flex">
          <Grid container height={"100%"} alignItems="stretch" justifyContent="stretch">
            <Grid item xs={6} display="flex" padding={theme.spacing(0, 1, 1, 0)}>
              <BybVrCard sx={{ width: "100%" }}>
                <Stack spacing={1} width="100%" justifyContent="space-between">
                  <Typography variant="text1xl400" color="text.secondary" textAlign="left">
                    {i18n("progress.training.streak")}
                  </Typography>
                  {isStreakLoading ? <Skeleton variant="text" width={100} height={48} /> : <TimeDisplay weeks={trainingStreakWeeks} />}
                </Stack>
              </BybVrCard>
            </Grid>

            <Grid item xs={6} display="flex" padding={theme.spacing(0, 0, 1, 1)}>
              <BybVrCard sx={{ width: "100%" }}>
                <Stack spacing={1} width="100%" justifyContent="space-between">
                  <Typography variant="text1xl400" color="text.secondary" textAlign="left">
                    {i18n("progress.training.total-training-time")}
                  </Typography>
                  {isTotalTimeLoading ? (
                    <Skeleton variant="text" width={100} height={48} />
                  ) : (
                    <TimeDisplay hours={totalHours} minutes={totalMinutes} />
                  )}
                </Stack>
              </BybVrCard>
            </Grid>

            <Grid item xs={6} display="flex" padding={theme.spacing(1, 1, 0, 0)}>
              <BybVrCard sx={{ width: "100%" }}>
                <Stack spacing={1} width="100%" justifyContent="space-between">
                  <Typography variant="text1xl400" color="text.secondary" textAlign="left">
                    {i18n("progress.training.avg-session-duration")}
                  </Typography>
                  <TimeDisplay minutes={avgSessionMinutes} seconds={avgSessionSeconds} />
                </Stack>
              </BybVrCard>
            </Grid>

            <Grid item xs={6} display="flex" padding={theme.spacing(1, 0, 0, 1)}>
              <BybVrCard sx={{ width: "100%" }}>
                <Stack spacing={1} width="100%" justifyContent="space-between">
                  <Typography variant="text1xl400" color="text.secondary" textAlign="left">
                    {i18n("progress.training.total-sessions")}
                  </Typography>
                  {isTotalSessionsLoading ? (
                    <Skeleton variant="text" width={80} height={48} />
                  ) : (
                    <Typography variant="text3xl700" color="dsBase.v200" textAlign="left">
                      {totalSessions}
                    </Typography>
                  )}
                </Stack>
              </BybVrCard>
            </Grid>
          </Grid>
        </Grid>
      </Grid>
    </Stack>
  );
};

export default TrainingStatsSection;
